/**
 * todo: refactor for loops to enable eslint back
 */
/* eslint-disable no-restricted-syntax */
import { Entries } from 'type-fest';
import { z, ZodFirstPartyTypeKind, ZodObject, ZodTypeAny } from 'zod';

import { AnyObject, JSONSchema, Schema, SchemaProperties } from '@typings';
import { APP_INSTANCE_REF_LITERAL, CUSTOM_COMPONENT_NAME } from '@constants';
import {
  as,
  capitalize,
  conditionalProperty,
  getPatternErrorMessage,
  isArray,
  isNumber,
  isObject,
  joinNonEmptyStrings,
  unflattenObject,
} from '@utils';
import {
  isSchemaAnyOf,
  isSchemaArray,
  isSchemaContainer,
  isSchemaField,
  isSchemaWithChildren,
} from '@utils/guards';

type RequiredSchema = Record<string, boolean>;

type SchemaContext = {
  parent?: string;
  requiredSchema: RequiredSchema;
};

type Model = Record<string, any>;

type ModelChildren = Schema.ModelWithChildren['children'];

type Arguments = {
  schema: Model;
  root: JSONSchema;
  context: SchemaContext;
  debug?: string;
  /**
   * JSON Schema definition type name
   */
  typeName?: string;
  prefix?: string;
  shouldCheckParentRequired?: boolean;
  schemaDefaultValues?: AnyObject;
};

const extractMetadata = (schema: Model) => {
  if (!isObject(schema)) {
    return {};
  }

  const {
    title,
    'x-title': xTitle,
    description,
    'x-description': xDescription,
    'x-is-advanced-field': xIsAdvancedField,
  } = schema as any;

  return {
    ...conditionalProperty('title', xTitle || title),
    ...conditionalProperty('description', xDescription || description),
    ...conditionalProperty('isCollapsibleField', xIsAdvancedField),
  };
};

function resolveRef(
  ref: string,
  schema: JSONSchema,
): { type: string; schema: JSONSchema } {
  const type = ref.replace(/^#\/\$defs\//, '');

  return { schema: schema.$defs[type], type };
}

/**
 * External properties are properties with `x-` prefix
 */
const extractExternalProperties = (schema: Model) => {
  const properties = (
    Object.entries(schema) as Entries<SchemaProperties.Properties>
  ).reduce((properties, [property, value]) => {
    if (property.startsWith('x-')) {
      properties[property] = value;
    }

    return properties;
  }, {} as SchemaProperties.Properties);

  return properties;
};

const extractMetaTypeProperty = (
  properties: SchemaProperties.Properties | Model,
): SchemaProperties.Properties['x-meta-type'] => properties['x-meta-type'];

/**
 * Get type properties which are located along with $ref
 * to override them in resolved schema
 */
const getInheritedProperties = (schema: Model) => {
  const properties = extractExternalProperties(schema);

  if ('default' in schema) {
    properties.default = schema.default;
  }

  return properties;
};

const getDefaultValue = (
  name: string,
  defaultValues: AnyObject | null | undefined,
  ...values: unknown[]
): any => {
  if (isObject(defaultValues) && name in defaultValues) {
    return defaultValues[name];
  }

  return values.find((value) => value !== undefined);
};

function extractProperties({
  schema,
  root,
  context,
  prefix = '',
  typeName,
  shouldCheckParentRequired = true,
  schemaDefaultValues,
}: Arguments): Schema.Model {
  const metadata = extractMetadata(schema);
  const children: ModelChildren = [];

  const result = {} as Schema.Model;

  Object.assign(result, metadata);

  /**
   * `anyOf` `null` node use-case
   * Fill empty `null` node with properties for form
   */
  if (schema.type === 'null') {
    const name = joinNonEmptyStrings(prefix, schema.type);

    Object.assign(result, {
      type: Schema.Type.Field,
      name,
      title: 'None',
      typeName,
      properties: {
        title: schema.title,
        description: schema.description,
        type: schema.type,
        name,
        required: false,
      },
    });

    return result;
  }

  /**
   * Currently, it is caught by `anyOf` children only
   */
  if (schema.type && schema.type !== 'object') {
    const name = joinNonEmptyStrings(prefix);
    const required = schema.required?.includes(prefix) ?? false;
    const isEnum = isArray(schema.enum);
    const type = isEnum ? 'enum' : schema.type;

    Object.assign(result, {
      ...conditionalProperty(
        'metaType',
        extractMetaTypeProperty(extractExternalProperties(schema)),
      ),
      type: Schema.Type.Field,
      name,
      typeName,
      title: schema.title ?? capitalize(schema.type),
      properties: {
        ...extractMetadata(schema),
        const: schema.const,
        default: schema.default,
        type,
        items: schema.items,
        options: schema.enum,
        name,
        required,
      },
    });

    return result;
  }

  if (schema.$ref) {
    const { type, schema: resolvedSchema } = resolveRef(schema.$ref, root);
    const inheritedProperties = getInheritedProperties(schema);
    const isEnum = isArray(resolvedSchema.enum);
    const nodeType = isEnum ? Schema.Type.Field : Schema.Type.Container;

    return Object.assign(result, {
      ...extractMetadata(resolvedSchema),
      ...extractProperties({
        schema: resolvedSchema,
        root,
        context,
        prefix,
        typeName: type,
      }),
      ...extractMetadata(inheritedProperties),
      ...conditionalProperty(
        'metaType',
        extractMetaTypeProperty({ ...resolvedSchema, ...inheritedProperties }),
      ),
      type: nodeType,
      typeName: type,
      name: prefix,
    });
  }

  if (schema.properties) {
    for (const [key, propSchema] of Object.entries<any>(schema.properties)) {
      const fullKeyName = joinNonEmptyStrings(prefix, key);
      const inheritedProperties = getInheritedProperties(propSchema);
      /**
       * Let parent to be required (when top properties are extracting)
       * on initial properties extraction.
       * This keeps nested `required` properties intact
       */
      const isParentRequired = (() => {
        if (!shouldCheckParentRequired) {
          return true;
        }

        if (!prefix) {
          return false;
        }

        return Boolean(context.requiredSchema[prefix]);
      })();
      const required = isParentRequired
        ? schema.required?.includes(key) ?? false
        : false;

      context.requiredSchema[fullKeyName] = required;

      if (propSchema.$ref) {
        const { type, schema: resolvedSchema } = resolveRef(
          propSchema.$ref,
          root,
        );

        const isEnum = isArray(resolvedSchema.enum);

        if (isEnum) {
          const { description, title } = {
            ...extractMetadata(propSchema),
            ...extractMetadata(inheritedProperties),
          };

          children.push({
            ...conditionalProperty(
              'metaType',
              extractMetaTypeProperty({
                ...propSchema,
                ...inheritedProperties,
              }),
            ),
            type: Schema.Type.Field,
            typeName: type,
            name: fullKeyName,
            properties: {
              type: 'enum',
              title,
              description,
              name: fullKeyName,
              options: resolvedSchema.enum,
              required,
              default: getDefaultValue(
                key,
                { ...inheritedProperties, ...schemaDefaultValues },
                propSchema.const,
                propSchema.default,
              ),
            },
          });
        } else if (resolvedSchema) {
          children.push({
            ...extractMetadata(resolvedSchema),
            ...extractMetadata(inheritedProperties),
            ...conditionalProperty(
              'metaType',
              extractMetaTypeProperty({
                ...resolvedSchema,
                ...inheritedProperties,
              }),
            ),
            type: Schema.Type.Container,
            typeName: type,
            name: fullKeyName,
            children: (
              extractProperties({
                schema: resolvedSchema,
                root,
                context,
                prefix: fullKeyName,
                typeName: type,
                schemaDefaultValues: inheritedProperties.default,
              }) as Schema.ModelWithChildren
            ).children,
          });
        }
      } else if (propSchema.anyOf) {
        const anyOfChildren = propSchema.anyOf.map((schema: JSONSchema) =>
          extractProperties({
            schema,
            root,
            context,
            prefix: fullKeyName,
            typeName,
          }),
        );

        children.push({
          type: Schema.Type.AnyOf,
          name: fullKeyName,
          ...conditionalProperty(
            'metaType',
            extractMetaTypeProperty({
              ...propSchema,
              ...inheritedProperties,
            }),
          ),
          ...extractMetadata(propSchema),
          typeName,
          children: anyOfChildren,
          ...(propSchema.default !== undefined && {
            default: propSchema.default,
          }),
        });
      } else if (propSchema.type) {
        const isArray = propSchema.type === 'array';
        const isArrayWithReference = isArray && !!propSchema.items.$ref;
        const arrayOf = isArray ? propSchema.items : null;

        if (isArrayWithReference) {
          const { type, schema: resolvedSchema } = resolveRef(
            propSchema.items.$ref,
            root,
          );
          /**
           * To build UI Schemas nested form components,
           * replace `$index` with useFieldArrays indexes
           */
          const arrayName = joinNonEmptyStrings(fullKeyName, '$index');
          const inheritedProperties = getInheritedProperties(propSchema);

          children.push({
            ...propSchema,
            ...extractMetadata(propSchema),
            ...inheritedProperties,
            ...conditionalProperty(
              'metaType',
              extractMetaTypeProperty({
                ...propSchema,
                ...inheritedProperties,
              }),
            ),
            type: Schema.Type.Array,
            typeName: type,
            name: fullKeyName,
            children: (
              extractProperties({
                schema: resolvedSchema,
                root,
                context,
                prefix: arrayName,
                typeName: type,
                shouldCheckParentRequired: false,
              }) as Schema.ModelWithChildren
            ).children,
          });
        } else {
          children.push({
            type: Schema.Type.Field,
            typeName,
            name: fullKeyName,
            properties: {
              ...propSchema,
              ...extractMetadata(propSchema),
              ...conditionalProperty('arrayOf', arrayOf),
              name: fullKeyName,
              default: getDefaultValue(
                key,
                schemaDefaultValues,
                propSchema.const,
                propSchema.default,
              ),
              required,
            },
          });
        }
      }
    }
  }

  if (isSchemaWithChildren(result)) {
    result.children = children;
  }

  return result;
}

function extractChildrenFields(children: ModelChildren): Schema.Field[] {
  return children
    .map((child) => {
      if (isSchemaWithChildren(child)) {
        return extractChildrenFields(child.children);
      }

      if (child.type === Schema.Type.Array) {
        return [];
      }

      return child;
    })
    .flat();
}

export function extractSchema(jsonSchema: JSONSchema) {
  if (!jsonSchema.properties) {
    return {
      uiSchemas: [],
      params: {},
    };
  }

  const requiredSchema = as
    .a<string[]>(jsonSchema.required)
    .reduce<RequiredSchema>(
      (schema, field) => ({ ...schema, [field]: true }),
      {},
    );

  const context: SchemaContext = {
    requiredSchema,
  };

  const uiSchemas = (
    extractProperties({
      schema: jsonSchema,
      root: jsonSchema,
      context,
      shouldCheckParentRequired: false,
    }) as Schema.ModelWithChildren
  ).children;

  const params = uiSchemas.reduce(
    (params, schema) => {
      if (!isSchemaWithChildren(schema)) {
        return params;
      }

      const fields = extractChildrenFields(schema.children).reduce(
        (fields, { properties }) => ({
          ...fields,
          [properties.name]: properties,
        }),
        {},
      );

      return { ...params, ...fields };
    },
    {} as Record<string, Schema.FieldProperties>,
  );

  return { uiSchemas, params };
}

function buildPrimitiveSchema(props: Schema.FieldProperties): ZodTypeAny {
  const {
    type,
    options,
    pattern,
    required,
    default: defaultValue,
    const: constValue,
  } = props;

  let schema: ZodTypeAny;

  if (constValue !== undefined) {
    schema = z.literal(constValue).default(constValue);

    return schema;
  }

  switch (type) {
    case 'string':
      schema = required ? z.string().min(1, 'Required') : z.string();

      if (pattern) {
        try {
          const regex = new RegExp(pattern);
          schema = (schema as z.ZodString).regex(regex, {
            message: getPatternErrorMessage(pattern),
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`Invalid RegExp pattern: ${pattern}`);
        }
      }
      break;
    case 'number':
    case 'float':
    case 'integer':
      schema = required
        ? z.number().refine(isNumber, { message: 'Required' })
        : z.number();
      break;
    case 'boolean':
      schema = z.boolean();
      break;
    case 'enum':
      if (options && options.length > 0) {
        schema = z.enum([...options] as [string, ...string[]]);
      } else {
        schema = z.string();
      }
      break;
    case 'null':
      schema = z.null();
      break;
    default:
      schema = z.unknown();
  }

  if (!required) {
    schema = schema.optional();
  }

  if (defaultValue !== undefined && defaultValue !== null) {
    schema = schema.default(defaultValue);
  }

  return schema;
}

const appIntegrationSchema = z.object({
  type: z.literal(APP_INSTANCE_REF_LITERAL),
  instance_id: z.string(),
  path: z.string(),
});

export const isPlatformAppsCustomComponent = (typeName: string | undefined) =>
  Object.values(CUSTOM_COMPONENT_NAME).some(([name]) => name === typeName);

export const isIntegrationType = (type?: string) =>
  type === Schema.InputMetaType.Integration ||
  type === Schema.InputMetaType.Dynamic;

export const isIntegrationTypeNode = (node: Schema.Model) =>
  isIntegrationType(node.metaType);

function buildSchemaShape(
  nodes: Schema.Model[],
): ZodObject<any, any, any, any, any>;
function buildSchemaShape(node: Schema.Model): ZodTypeAny;
function buildSchemaShape(input: Schema.Model | Schema.Model[]): ZodTypeAny {
  /**
   * Handles root schemas level
   */
  if (isArray(input)) {
    const shape = input.reduce<Record<string, ZodTypeAny>>((acc, node) => {
      const key = node.name.split('.').pop()!;

      acc[key] = buildSchemaShape(node);

      return acc;
    }, {});

    return z.object(shape);
  }

  /**
   * Handles nested schema node
   */
  const node = input;

  if (isSchemaContainer(node)) {
    if (isIntegrationTypeNode(node)) {
      return z.union([
        appIntegrationSchema,
        buildSchemaShape(node.children) as ZodObject<any>,
      ]);
    }

    return buildSchemaShape(node.children);
  }

  if (isSchemaField(node)) {
    return buildPrimitiveSchema(node.properties);
  }

  if (isSchemaAnyOf(node)) {
    const childSchemas = node.children.map((child) => {
      return buildSchemaShape(child);
    });

    // @ts-expect-error todo: fix type
    const union = z.union(childSchemas as [ZodTypeAny, ...ZodTypeAny[]]);

    return node.default !== undefined ? union.default(node.default) : union;
  }

  if (isSchemaArray(node)) {
    let elementSchema: ZodTypeAny = z.any();

    if (node.children.length > 1) {
      const shape = Object.fromEntries(
        node.children.map((child) => [
          child.name.split('.').pop()!,
          buildSchemaShape(child),
        ]),
      );

      elementSchema = z.object(shape);
    } else if (node.children.length === 1) {
      elementSchema = buildSchemaShape(node.children[0]);
    }

    const array = z.array(elementSchema);

    return node.default !== undefined ? array.default(node.default) : array;
  }

  return z.unknown();
}

/**
 * Debug utility
 */
function isZodType(schema: any): schema is ZodTypeAny {
  return (
    schema &&
    typeof schema === 'object' &&
    '_def' in schema &&
    /**
     * `_def` is zod property
     */
    // eslint-disable-next-line no-underscore-dangle
    typeof schema._def === 'object'
  );
}

/**
 * Debug utility
 */
/* eslint-disable */
function walkZod(
  schema: unknown,
  path: string[] = [],
  result: Record<string, string> = {},
): Record<string, string> {
  if (!isZodType(schema)) {
    result[path.join('.')] = 'Not a Zod schema';
    return result;
  }

  const { typeName } = schema._def;

  switch (typeName) {
    case ZodFirstPartyTypeKind.ZodObject: {
      const shape = schema._def.shape();
      for (const key in shape) {
        walkZod(shape[key], [...path, key], result);
      }
      break;
    }

    case ZodFirstPartyTypeKind.ZodOptional:
    case ZodFirstPartyTypeKind.ZodNullable:
      walkZod(schema._def.innerType, path, result);
      break;

    case ZodFirstPartyTypeKind.ZodUnion:
      schema._def.options.forEach((option: ZodTypeAny, index: number) => {
        walkZod(option, [...path, `option${index}`], result);
      });
      break;

    default:
      result[path.join('.')] = typeName;
      break;
  }

  return result;
}
/* eslint-enable */

export const getSchemaDefaultValues = (uiSchemas: Schema.Model[]) => {
  type DefaultValueModel = { name: string; default: any };
  const extractNodeDefaultValue = (node: Schema.Model): DefaultValueModel[] => {
    if (isSchemaField(node)) {
      return [{ name: node.name, default: node.properties.default }];
    }

    if (isSchemaArray(node) || isSchemaAnyOf(node)) {
      return [{ name: node.name, default: node.default }];
    }

    const containerDefaultValues = node.children.map(extractNodeDefaultValue);

    return containerDefaultValues.flat(10);
  };

  const values = uiSchemas.reduce(
    (values, node) => {
      const nodeDefaultValues = extractNodeDefaultValue(node);

      nodeDefaultValues.forEach(({ name, default: defaultValue }) => {
        if (defaultValue !== undefined) {
          values[name] = defaultValue;
        }
      });

      return values;
    },
    {} as Record<string, any>,
  );

  return unflattenObject(values);
};

type ExtractAppParamsSchema = {
  params: Record<string, any>;
  root: JSONSchema;
  schema: JSONSchema;
  prefix?: string;
  type?: string;
};
export function extractParamsSchema({
  params,
  root,
  schema,
  prefix = '',
  type = '',
}: ExtractAppParamsSchema): void {
  if (!schema || typeof schema !== 'object') return;

  if (schema.$ref) {
    const { schema: resolved, type } = resolveRef(schema.$ref, root);
    extractParamsSchema({ params, root, schema: resolved, prefix, type });
    return;
  }

  if (Array.isArray(schema.anyOf)) {
    const refEntry = schema.anyOf.find((entry) => '$ref' in entry) as
      | JSONSchema
      | undefined;
    if (refEntry?.$ref) {
      const { schema: resolved, type } = resolveRef(refEntry.$ref, root);

      extractParamsSchema({ params, root, schema: resolved, prefix, type });

      return;
    }
  }

  const meta = {
    ...extractMetadata(schema),
    type,
  };

  if (Object.keys(meta).length > 0 || prefix) {
    params[prefix] = meta;
  }

  if (schema.properties) {
    Object.entries(schema.properties).forEach(([key, value]) => {
      extractParamsSchema({
        params,
        root,
        schema: value,
        prefix: joinNonEmptyStrings(prefix, key),
        type,
      });
    });
  }

  if (schema.type === 'array' && isObject(schema.items)) {
    extractParamsSchema({
      params,
      root,
      schema: schema.items as JSONSchema,
      prefix: joinNonEmptyStrings(prefix, '[]'),
      type,
    });
  }
}

export const getParamsSchema = (schema: JSONSchema) => {
  const params = {};

  extractParamsSchema({ params, root: schema, schema });

  return params;
};

export const extractIngerationTypesFromSchema = (
  schema: JSONSchema | null,
  uiSchemasArgument: Schema.Model[] = [],
): Record<string, Schema.Model> => {
  const uiSchemas = schema
    ? extractSchema(schema).uiSchemas
    : uiSchemasArgument;
  const result: Record<string, Schema.Model> = {};

  const traverse = (schemas: Schema.Model[]): void => {
    schemas.forEach((schema) => {
      if (isIntegrationTypeNode(schema)) {
        result[schema.name] = schema;
      }

      if (
        isSchemaContainer(schema) ||
        isSchemaAnyOf(schema) ||
        isSchemaArray(schema)
      ) {
        traverse(schema.children);
      }
    });
  };

  traverse(uiSchemas);

  return result;
};

export function getResolverSchema(uiSchemas: Schema.Model[]) {
  const schemaShape = buildSchemaShape(uiSchemas);

  return schemaShape.extend({
    displayName: z.string(),
  });
}
